import { Calendar, List, Columns, LayoutGrid } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ViewMode = 'list' | 'card' | 'calendar' | 'kanban';

interface ViewToggleProps {
  view: ViewMode;
  onViewChange: (view: ViewMode) => void;
  availableViews?: ViewMode[]; // Which views are actually implemented
}

const VIEW_CONFIG: Record<ViewMode, { icon: typeof List; title: string }> = {
  list: { icon: List, title: 'List view' },
  card: { icon: LayoutGrid, title: 'Card view' },
  calendar: { icon: Calendar, title: 'Calendar view' },
  kanban: { icon: Columns, title: 'Kanban view' },
};

export function ViewToggle({ view, onViewChange, availableViews = ['list', 'calendar', 'kanban'] }: ViewToggleProps) {
  // Only show views that are available
  const viewsToShow = availableViews.filter((v) => Object.keys(VIEW_CONFIG).includes(v));

  // If only one view is available, don't show the toggle at all
  if (viewsToShow.length <= 1) {
    return null;
  }

  return (
    <div className="inline-flex items-center rounded-md border border-input bg-background p-1">
      {viewsToShow.map((viewMode) => {
        const config = VIEW_CONFIG[viewMode];
        const Icon = config.icon;
        const isActive = view === viewMode;

        return (
          <button
            key={viewMode}
            type="button"
            onClick={() => onViewChange(viewMode)}
            className={cn(
              'inline-flex items-center justify-center rounded-sm px-2.5 py-1.5 text-sm font-medium transition-colors',
              isActive
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
            aria-pressed={isActive}
            title={config.title}
          >
            <Icon className="h-4 w-4" />
            <span className="sr-only">{config.title}</span>
          </button>
        );
      })}
    </div>
  );
}
